import type {ReactElement} from 'react';
import {Button, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {useIsMobileWidth} from '@Hooks/useIsMobileWidth';
import {isPostPublishStatusInFlight, PostPublishStatus} from '@Libraries/social-media-publish-status.library';
import type {PostPublishStatusActionConfig, PostPublishStatusRowProps, ScheduledSubtitleRuns} from './post-publish-status-row.types';
import {POST_PUBLISH_STATUS_CONFIG, SCHEDULED_DATE_TIME_SPLIT_MARKER} from './post-publish-status-row.types';
import styles from './post-publish-status-row.module.scss';

export function PostPublishStatusRow({
  status,
  scheduledDateTimeLabel = '',
  publishedActionLabel,
  errorTitle = '',
  errorDescription = '',
  isActionDisabled = false,
  onActionClick,
  'data-testid': dataTestId,
}: PostPublishStatusRowProps): ReactElement {
  const isMobileWidth = useIsMobileWidth();
  const isInFlight = isPostPublishStatusInFlight(status);
  const statusConfig = POST_PUBLISH_STATUS_CONFIG[status];

  const getStatusIconCircle = (): ReactElement => {
    return (
      <span className={`${styles.statusIconCircle} ${styles[`${statusConfig.toneClass}StatusIconCircle`]}`}>
        <Icon icon={statusConfig.glyph} size={IconSize.SIZE_ICON_20} className={isInFlight ? styles.spinningStatusGlyph : ''} ariaHidden />
      </span>
    );
  };

  const getStatusTitle = (): string => {
    if (status === PostPublishStatus.FAILED && errorTitle !== '') {
      return errorTitle;
    }

    return window.i18next.t(statusConfig.titleKey);
  };

  const getPlainSubtitle = (subtitle: string): ReactElement => {
    return <Text val={subtitle} size={TextSize.MD} className={styles.statusSubtitleText} />;
  };

  const getScheduledSubtitleRuns = (): ScheduledSubtitleRuns => {
    const sentenceRuns = window.i18next.t('pmwjs_social_post_scheduled_will_go_live_on', {dateTime: SCHEDULED_DATE_TIME_SPLIT_MARKER}).split(SCHEDULED_DATE_TIME_SPLIT_MARKER);

    return {textBeforeDateTime: sentenceRuns[0], textAfterDateTime: sentenceRuns.length > 1 ? sentenceRuns[1] : ''};
  };

  const getScheduledSubtitle = (): ReactElement => {
    const {textBeforeDateTime, textAfterDateTime} = getScheduledSubtitleRuns();

    return (
      <p className={styles.statusSubtitleParagraph}>
        <Text val={textBeforeDateTime} size={TextSize.MD} className={styles.statusSubtitleText} />
        <Text val={scheduledDateTimeLabel} size={TextSize.MD} bold className={styles.statusSubtitleDateTime} />
        <Text val={textAfterDateTime} size={TextSize.MD} className={styles.statusSubtitleText} />
      </p>
    );
  };

  const getStatusSubtitle = (): ReactElement => {
    if (status === PostPublishStatus.SCHEDULED) {
      return getScheduledSubtitle();
    }

    if (status === PostPublishStatus.FAILED) {
      return getPlainSubtitle(errorDescription);
    }

    return getPlainSubtitle(window.i18next.t(statusConfig.subtitleKey));
  };

  const getActionConfig = (): PostPublishStatusActionConfig => {
    const label = status === PostPublishStatus.PUBLISHED ? (publishedActionLabel ?? window.i18next.t(statusConfig.actionLabelKey)) : window.i18next.t(statusConfig.actionLabelKey);

    return {label, icon: statusConfig.actionIcon, iconPlacement: statusConfig.actionIconPlacement};
  };

  const getActionButton = (): ReactElement => {
    const {label, icon, iconPlacement} = getActionConfig();

    return (
      <div className={`${styles.statusActionSlot} ${isInFlight ? styles.hiddenStatusActionSlot : ''}`} aria-hidden={isInFlight}>
        <Button
          type={Type.SECONDARY}
          size={isMobileWidth ? Size.XSMALL : Size.SMALL}
          text={label}
          icon={icon}
          iconPlacement={iconPlacement}
          disabled={isActionDisabled || isInFlight}
          onClick={onActionClick}
        />
      </div>
    );
  };

  return (
    <div className={styles.postPublishStatusRow} data-testid={dataTestId}>
      <div className={styles.statusSummary}>
        {getStatusIconCircle()}
        <div className={styles.statusTextStack}>
          <Text val={getStatusTitle()} size={TextSize.MD} bold className={styles.statusTitle} />
          {getStatusSubtitle()}
        </div>
      </div>
      {getActionButton()}
    </div>
  );
}
